import React, { useMemo } from 'react';
import ReactDOM from 'react-dom';
import './tour-confetti.scss';

/* TourConfetti
   ------------
   Pure CSS celebration confetti shown when the user reaches the final
   step of the guided tour ("Congratulations 🎉"). No extra dependency —
   we render ~80 small coloured rectangles via a portal to document.body
   and animate each with `translateY + rotate` keyframes whose parameters
   come from per-piece CSS custom properties (random `left`, `delay`,
   `duration`, `color`, `drift`, `rotate`). pointer-events:none so it
   never blocks interaction with the Finish Tour button. */

const COLORS = [
  /* Violet palette to match the rest of the tour theme */
  '#a855f7', '#7c3aed', '#6d28d9', '#c084fc',
  /* Bright accents for the celebration pop */
  '#ec4899', '#f59e0b', '#10b981',
  '#3b82f6', '#fbbf24', '#ef4444',
];

const PIECE_COUNT = 80;

/* Memoised so the pieces' random parameters are generated ONCE per
   mount of TourConfetti. If we computed them inline every render the
   animation would restart on every parent re-render. */
const useConfettiPieces = () => useMemo(() => (
  Array.from({ length: PIECE_COUNT }, (_, i) => ({
    key: i,
    left: Math.random() * 100,           // % across viewport
    delay: Math.random() * 1.2,          // seconds before drop starts
    duration: 2.5 + Math.random() * 2,   // seconds to fall
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    size: 6 + Math.random() * 6,         // px square base
    rotate: 360 + Math.random() * 720,   // total rotation in deg
    drift: -80 + Math.random() * 160,    // -80..80 px sideways drift
  }))
), []);

export const TourConfetti: React.FC = () => {
  const pieces = useConfettiPieces();

  if (typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <div className="tour-confetti" aria-hidden>
      {pieces.map((p) => (
        <span
          key={p.key}
          className="tour-confetti__piece"
          style={{
            left: `${p.left}%`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            background: p.color,
            width: `${p.size}px`,
            height: `${p.size * 1.6}px`,
            // CSS custom properties consumed by the keyframe
            ['--drift' as any]: `${p.drift}px`,
            ['--rotate' as any]: `${p.rotate}deg`,
          }}
        />
      ))}
    </div>,
    document.body
  );
};
